// タイムライン関連の型定義

// 基本的な投稿型（互換性のため）
export interface TimelinePost {
  id: number;
  title?: string;
  content: string;
  category: PostCategory;
  target_audience: TargetAudience;
  target_conditions?: any;
  start_date?: string;
  end_date?: string;
  status: PostStatus;
  published_at?: string;
  expires_at?: string;
  view_count: number;
  like_count: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  company: TimelineCompany;
  author: TimelineAuthor;
  is_liked_by_user?: boolean;
  image_url?: string;
  publication_end?: string;
}

// 企業情報型
export interface TimelineCompany {
  id: number;
  name: string;
  logo_url?: string;
}

// 投稿者情報型
export interface TimelineAuthor {
  id: number;
  name: string;
  full_name?: string;
  title?: string;
}

// ページネーション型
export interface TimelinePagination {
  current_page: number;
  total_pages: number;
  total_count: number;
  per_page: number;
}

// タイムライン取得レスポンス型（互換性のため）
export interface GetTimelineResponse {
  success: boolean;
  data: {
    posts: TimelinePost[];
    pagination: TimelinePagination;
  };
}

// タイムライン検索クエリ型
export interface TimelineQuery {
  page?: number;
  limit?: number;
  category?: PostCategory;
  company_id?: number;
  search?: string;
}

export enum PostCategory {
  RECRUITMENT = 'recruitment', // 採用情報
  EVENT = 'event', // イベント
  COMPANY_NEWS = 'company_news', // 企業ニュース
  OTHER = 'other', // その他
}

export enum TargetAudience {
  ALL_STUDENTS = 'all_students', // 全学生
  SPECIFIC_CONDITIONS = 'specific_conditions', // 特定条件の学生
}

export enum PostStatus {
  DRAFT = 'draft', // 下書き
  PUBLISHED = 'published', // 公開中
  EXPIRED = 'expired', // 公開期間終了
}

// 投稿作成リクエスト
export interface CreateTimelinePostRequest {
  title?: string;
  content: string;
  category: PostCategory;
  target_audience?: TargetAudience;
  target_conditions?: any;
  start_date?: string; // ISO date string
  end_date?: string; // ISO date string
  image_url?: string; // 投稿画像URL
}

// 投稿更新リクエスト
export interface UpdateTimelinePostRequest {
  title?: string;
  content?: string;
  category?: PostCategory;
  target_audience?: TargetAudience;
  target_conditions?: any;
  start_date?: string;
  end_date?: string;
  status?: PostStatus;
  image_url?: string;
}

// 投稿一覧取得クエリ
export interface GetTimelinePostsQuery {
  page?: number;
  limit?: number;
  category?: PostCategory; // 後方互換性のため残す
  categories?: PostCategory[]; // 複数カテゴリ対応
  status?: PostStatus;
  company_id?: number;
  search?: string;
}

// 投稿詳細情報
export interface TimelinePostDetail {
  id: number;
  title?: string;
  content: string;
  category: PostCategory;
  target_audience: TargetAudience;
  target_conditions?: any;
  start_date?: string;
  end_date?: string;
  status: PostStatus;
  published_at?: string;
  expires_at?: string;
  view_count: number;
  like_count: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
  image_url?: string; // 投稿画像URL
  company: {
    id: number;
    name: string;
    logo_url?: string;
  };
  author: {
    id: number;
    name: string;
  };
  is_liked_by_user?: boolean; // 学生用：現在のユーザーがいいね済みか
}

// 投稿作成レスポンス
export interface CreateTimelinePostResponse {
  success: boolean;
  message: string;
  data: {
    post_id: number;
    status: PostStatus;
    published_at?: string;
  };
}

// 投稿一覧レスポンス
export interface GetTimelinePostsResponse {
  success: boolean;
  data: {
    posts: TimelinePostDetail[];
    pagination: {
      current_page: number;
      total_pages: number;
      total_count: number;
      per_page: number;
    };
  };
}

// 投稿詳細レスポンス
export interface GetTimelinePostResponse {
  success: boolean;
  data: TimelinePostDetail;
}

// いいね追加・削除レスポンス
export interface LikePostResponse {
  success: boolean;
  message: string;
  data: {
    post_id: number;
    is_liked: boolean;
    like_count: number;
  };
}

// 投稿削除レスポンス
export interface DeleteTimelinePostResponse {
  success: boolean;
  message: string;
}

// 企業投稿統計
export interface CompanyPostStatsResponse {
  success: boolean;
  data: {
    total_posts: number;
    published_posts: number;
    draft_posts: number;
    total_views: number;
    total_likes: number;
    avg_engagement_rate: number;
  };
}

// カテゴリ表示名のマッピング
export const PostCategoryLabels: Record<PostCategory, string> = {
  [PostCategory.RECRUITMENT]: '採用情報',
  [PostCategory.EVENT]: 'イベント',
  [PostCategory.COMPANY_NEWS]: '企業ニュース',
  [PostCategory.OTHER]: 'その他',
};

// 旧名称との互換性のため
export const POST_CATEGORY_LABELS = PostCategoryLabels;

// カテゴリカラーのマッピング
export const PostCategoryColors: Record<PostCategory, string> = {
  [PostCategory.RECRUITMENT]: '#3b82f6', // blue-500
  [PostCategory.EVENT]: '#10b981', // emerald-500
  [PostCategory.COMPANY_NEWS]: '#f59e0b', // amber-500
  [PostCategory.OTHER]: '#6b7280', // gray-500
};

// 旧名称との互換性のため
export const POST_CATEGORY_COLORS = PostCategoryColors;

// ステータス表示名のマッピング
export const PostStatusLabels: Record<PostStatus, string> = {
  [PostStatus.DRAFT]: '下書き',
  [PostStatus.PUBLISHED]: '公開中',
  [PostStatus.EXPIRED]: '公開期間終了',
};

// 公開対象表示名のマッピング
export const TargetAudienceLabels: Record<TargetAudience, string> = {
  [TargetAudience.ALL_STUDENTS]: '全学生',
  [TargetAudience.SPECIFIC_CONDITIONS]: '特定条件の学生',
};
